import { getActivePhase, getActivePhaseWeek } from './protocolPhases';
import { dateActivationSuivi, debutPhaseActive } from '../suivi/services/suiviPhaseDates';

function hasPhaseRows(value) {
  return Boolean(value && Array.isArray(value.phases) && value.phases.length > 0);
}

function resolveTransitionTargetForGeneration(consultation) {
  const rawDraft = consultation?.followupData?.phaseTransitionDraft
    ?? consultation?.followup_data?.phaseTransitionDraft;
  if (rawDraft === null || rawDraft === undefined) return null;
  if (!rawDraft || typeof rawDraft !== 'object' || Array.isArray(rawDraft)) {
    throw new Error(
      'La preuve de la phase cible est invalide. Recharge la version avant de générer le plan.',
    );
  }

  const sourceConsultationId = String(rawDraft.sourceConsultationId || '').trim();
  const fromPhaseId = String(rawDraft.fromPhaseId || '').trim();
  const toPhaseId = String(rawDraft.toPhaseId || '').trim();
  const protocolPhases = consultation?.protocol_phases;
  const declaredActivePhaseId = String(
    consultation?.active_phase_id ?? consultation?.activePhaseId ?? '',
  ).trim();
  const declaredSourceConsultationId = String(
    consultation?.previousConsultationId ?? consultation?.previous_consultation_id ?? '',
  ).trim();
  if (!sourceConsultationId
      || !fromPhaseId
      || !toPhaseId
      || fromPhaseId === toPhaseId
      || !declaredActivePhaseId
      || fromPhaseId !== declaredActivePhaseId
      || !declaredSourceConsultationId
      || sourceConsultationId !== declaredSourceConsultationId
      || !hasPhaseRows(protocolPhases)) {
    throw new Error(
      'La phase cible ou sa configuration DB est indisponible. Recharge la version avant de générer le plan.',
    );
  }
  const sourcePhase = protocolPhases.phases.find(
    (phase) => String(phase?.id) === fromPhaseId,
  );
  const proposedPhase = protocolPhases.phases.find(
    (phase) => String(phase?.id) === toPhaseId,
  );
  const templateKey = typeof protocolPhases.template === 'string'
    ? protocolPhases.template.trim()
    : '';
  if (!sourcePhase
      || !proposedPhase
      || sourcePhase.status !== 'active'
      || proposedPhase.status !== 'upcoming'
      || !templateKey) {
    throw new Error(
      'La phase cible ou sa configuration DB est indisponible. Recharge la version avant de générer le plan.',
    );
  }

  return {
    protocolPhases,
    activePhase: {
      templateKey,
      phaseId: proposedPhase.id,
      weekNumber: undefined,
    },
  };
}

// datesActivation : table { phaseId: date } (lireDatesActivationPhases) pour une
// phase activée par le nouveau suivi, publication OU remise du document.
export function resolveProtocolPhaseForGeneration(consultation, client, { datesActivation = null } = {}) {
  const transitionTarget = resolveTransitionTargetForGeneration(consultation);
  if (transitionTarget) return transitionTarget;

  const consultationPhases = consultation?.protocol_phases;
  const clientPhases = client?.protocol_phases;
  const protocolPhases = hasPhaseRows(consultationPhases)
    ? consultationPhases
    : (hasPhaseRows(clientPhases) ? clientPhases : null);
  if (!protocolPhases) return { protocolPhases: null, activePhase: null };

  const activePhaseObject = getActivePhase(protocolPhases);
  const declaredActivePhaseId = consultation?.active_phase_id
    ?? consultation?.activePhaseId
    ?? client?.active_phase_id
    ?? client?.activePhaseId
    ?? null;
  if (declaredActivePhaseId !== null) {
    const declaredRow = protocolPhases.phases.find(
      (phase) => String(phase?.id) === String(declaredActivePhaseId),
    );
    if (!declaredRow || declaredRow.status !== 'active'
        || String(activePhaseObject?.id) !== String(declaredActivePhaseId)) {
      throw new Error(
        'La phase active enregistrée est incohérente. Anissa doit la vérifier avant de générer le plan.',
      );
    }
  }
  if (!activePhaseObject) return { protocolPhases, activePhase: null };

  const templateKey = typeof protocolPhases.template === 'string'
    ? protocolPhases.template.trim()
    : '';
  const phaseId = activePhaseObject.id;
  if (!templateKey || (typeof phaseId !== 'string' && typeof phaseId !== 'number')) {
    throw new Error(
      'La phase active est incomplète. Anissa doit confirmer sa configuration avant de générer le plan.',
    );
  }
  const weekInfo = getActivePhaseWeek(protocolPhases, {
    startedAt: debutPhaseActive(protocolPhases, consultation, datesActivation || dateActivationSuivi(consultation)),
  });
  return {
    protocolPhases,
    activePhase: {
      templateKey,
      phaseId,
      weekNumber: weekInfo?.weekNumber,
    },
  };
}
